import amplitude from 'amplitude-js';
import athenaConfig from '../config';
import { getClientId, getOktaUserId } from './widget-facade';
import { getAnetDb, getUserType } from './user-utils';
import {
  ERROR_PAGE_EVENT_PREFIX,
  MFA_CHALLENGE_EVENT_PREFIX,
  MFA_ENROLL_EVENT_PREFIX,
  OHSW_EVENT_PREFIX,
  PASSWORD_EXPIRED_EVENT_PREFIX
} from '../constants';
import { getAuthorizeUrl } from './storage-utils';

const url = window.location.href;
const userId = new URL(url).searchParams.get('amplitudeUserId');
const deviceId = new URL(url).searchParams.get('deviceId');
const amplitudeDeviceId = new URL(url).searchParams.get('amplitude_device_id');

const { amplitudeApiKey } = athenaConfig;

const loggedAmplitudeEvents: Record<string, number> = {};

amplitude.getInstance().init(amplitudeApiKey, userId!, {
  deviceId: amplitudeDeviceId ?? deviceId!,
});

const AmplitudeUtils = {
  isWhiteListedEvent: function (eventName: string) {
    // _AthenaIdentityAmplituedEventWhitelist is defined in
    // https://bitbucket.athenahealth.com/projects/IAM/repos/identity-amplitude-event-whitelist/browse
    return !!global._AthenaIdentityAmplituedEventWhitelist?.includes?.(eventName);
  },
  logForgeError: async function (options: Record<string, any>) {
    await this.logCustomEvent('ForgeError', options);
  },
  logErrorPageLanding: async function (options: Record<string, any>) {
    await this.logCustomEvent(`${ERROR_PAGE_EVENT_PREFIX}Landing`, options);
  },
  logErrorPageReloadPerformed: async function () {
    await this.logCustomEvent(`${ERROR_PAGE_EVENT_PREFIX}ReloadPerformed`, {});
  },
  logErrorPageAuthUrlMissing: async function () {
    await this.logCustomEvent(`${ERROR_PAGE_EVENT_PREFIX}AuthUrlMissing`, {});
  },
  logErrorPageError: async function (options: Record<string, any>) {
    await this.logCustomEvent(`${ERROR_PAGE_EVENT_PREFIX}Error`, options);
  },

  fillEventProperties: function (additionalProps: Record<string, any>) {
    const authorizeUrl = getAuthorizeUrl();
    const queryParams = authorizeUrl.searchParams;
    const eventProperties: Record<string, any> = {
      userId: getOktaUserId(),
      idp: queryParams.get('idp'),
      clientId: global.settings?.clientId ?? getClientId(global.OktaUtil?.getRequestContext()) ?? queryParams.get('client_id'),
      lwaLogin: global.settings?.isAuthnWithLwaClientId,
      lwaUniqId: global.settings?.lwaUniqId,
      appType: global.settings?.appType,
      supportEnv: global.settings?.supportEnv,
      workflowType: global.settings?.workflowType,
      anet_db: getAnetDb(),
      userType: getUserType(),
    };
    return { ...eventProperties, ...additionalProps };
  },
  logCustomEvent: function (eventName: string, additionalProps: object) {
    const finalEventName = `${OHSW_EVENT_PREFIX}${eventName}`;
    if (eventName && this.isWhiteListedEvent(finalEventName)) {
      // If the same event was logged in the last 100ms, filter it out.
      const currentTime = new Date().getTime();
      if (loggedAmplitudeEvents[finalEventName] && currentTime - loggedAmplitudeEvents[finalEventName] < 100) {
        return;
      }
      loggedAmplitudeEvents[finalEventName] = currentTime;
      const eventProperties = this.fillEventProperties({
        ...additionalProps,
      });

      return Promise.race([
        new Promise((resolve) => {
          amplitude.getInstance().logEvent(finalEventName, eventProperties, resolve);
        }),
        new Promise((resolve) => {
          setTimeout(resolve, 1000);
        }),
      ]);
    }
  },
  logPasswordResetEvent: async function (eventName: string, additionalProps: any) {
    await this.logCustomEvent(`${PASSWORD_EXPIRED_EVENT_PREFIX}${eventName}`, { ...additionalProps, isPasswordResetWorkflow: !!settings.stateHandle });
  },
  logIdpDiscoveryUserLoginEvent: async function (additionalProps: any) {
    await this.logCustomEvent('IdpDiscoveryUserLoginInitiated', additionalProps);
  },
  logCustomizerError: async function (props: Record<string, any>) {
    await this.logCustomEvent('CustomizerError', props);
  },
  logMfaEnrollEvent: async function(eventName: string) {
    await this.logCustomEvent(`${MFA_ENROLL_EVENT_PREFIX}${eventName}`, {});
  },
  logMfaChallengeEvent: async function(eventName: string) {
    await this.logCustomEvent(`${MFA_CHALLENGE_EVENT_PREFIX}${eventName}`, {});
  },
  logErrorBoundaryError: async function (props: Record<string, any>) {
    await this.logCustomEvent('ErrorBoundaryError', props);
  },
  logLaunchLwaError: async function (props: Record<string, any>) {
    await this.logCustomEvent('LaunchLwaError', props);
  },
};

export default AmplitudeUtils;
